import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ESCALAS, ESCALA_PADRAO, TamanhoTexto } from "@/components/blog/TamanhoTexto";
import { BlogBanner } from "@/components/blog/BlogBanner";
import { useSuspenseQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";

import { blogIndexQuery, blogPostQuery } from "@/lib/blog/queries";
import { Markdown } from "@/components/blog/Markdown";
import { ShareButtons } from "@/components/blog/ShareButtons";
import { PostCard } from "@/components/blog/PostCard";
import { PosCta } from "@/components/blog/PosCta";
import { EmResumo } from "@/components/blog/EmResumo";
import { ReadingProgress } from "@/components/blog/ReadingProgress";
import { Toc } from "@/components/blog/Toc";
import { IndiceArtigo } from "@/components/blog/IndiceArtigo";
import { AuthorBox } from "@/components/blog/AuthorBox";
import { BackToTop } from "@/components/blog/BackToTop";
import { extrairFaq, extrairResumo } from "@/lib/blog/resumo";
import { dividirNoMeio, extrairToc, tempoLeitura } from "@/lib/blog/leitura";
import { separarIntroducao } from "@/lib/blog/limpar-md";
import { jsonLd } from "@/lib/seo/json-ld";
import { Lightbox } from "@/components/blog/Lightbox";

const BASE = "https://fisioemortopedia.com.br";

export const Route = createFileRoute("/e-fisio/$slug")({
  loader: async ({ context, params }) => {
    const [data] = await Promise.all([
      context.queryClient.ensureQueryData(blogPostQuery(params.slug)),
      context.queryClient.ensureQueryData(blogIndexQuery),
    ]);
    if (!data.post) throw notFound();
    const cat = data.categories.find((c) => c.slug === data.post!.categoria_slugs?.[0]);
    return {
      titulo: data.post.seo_title || data.post.titulo,
      descricao: data.post.seo_description || data.post.subtitulo || "",
      capa: data.post.capa_url,
      slug: data.post.slug,
      published_at: data.post.published_at,
      updated_at: data.post.updated_at,
      autor: data.post.autor,
      categoria: cat ? { slug: cat.slug, nome: cat.nome } : null,
      faq: extrairFaq(data.post.conteudo_md ?? ""),
    };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [{ title: "Artigo não encontrado · E-FISIO" }, { name: "robots", content: "noindex" }],
      };
    }
    const url = `${BASE}/e-fisio/${loaderData.slug}`;
    const image = loaderData.capa ? `${BASE}${loaderData.capa}` : null;
    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "E-FISIO", item: `${BASE}/e-fisio` },
        ...(loaderData.categoria
          ? [
              {
                "@type": "ListItem",
                position: 2,
                name: loaderData.categoria.nome,
                item: `${BASE}/e-fisio/categoria/${loaderData.categoria.slug}`,
              },
            ]
          : []),
        {
          "@type": "ListItem",
          position: loaderData.categoria ? 3 : 2,
          name: loaderData.titulo,
          item: url,
        },
      ],
    };
    return {
      meta: [
        { title: loaderData.titulo },
        { name: "description", content: loaderData.descricao },
        { property: "og:title", content: loaderData.titulo },
        { property: "og:description", content: loaderData.descricao },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "article:published_time", content: loaderData.published_at },
        { property: "article:modified_time", content: loaderData.updated_at },
        ...(loaderData.categoria
          ? [{ property: "article:section", content: loaderData.categoria.nome }]
          : []),
        { name: "twitter:card", content: "summary_large_image" },
        ...(image
          ? [
              { property: "og:image", content: image },
              { name: "twitter:image", content: image },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: jsonLd({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: loaderData.titulo,
            description: loaderData.descricao,
            mainEntityOfPage: url,
            url,
            inLanguage: "pt-BR",
            datePublished: loaderData.published_at,
            dateModified: loaderData.updated_at,
            ...(image ? { image } : {}),
            ...(loaderData.categoria ? { articleSection: loaderData.categoria.nome } : {}),
            author: {
              "@type": loaderData.autor ? "Person" : "Organization",
              name: loaderData.autor || "Fisio em Ortopedia",
            },
            publisher: {
              "@type": "Organization",
              name: "Fisio em Ortopedia",
              url: BASE,
            },
          }),
        },
        { type: "application/ld+json", children: jsonLd(breadcrumb) },
        ...(loaderData.faq.length
          ? [
              {
                type: "application/ld+json",
                children: jsonLd({
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: loaderData.faq.map((f) => ({
                    "@type": "Question",
                    name: f.pergunta,
                    acceptedAnswer: { "@type": "Answer", text: f.resposta },
                  })),
                }),
              },
            ]
          : []),
      ],
    };
  },
  errorComponent: ArtigoErro,
  notFoundComponent: ArtigoNaoEncontrado,
  component: ArtigoPage,
});

function ArtigoErro() {
  return (
    <main className="container mx-auto px-4 py-24 text-center">
      <h1 className="text-2xl font-extrabold text-[var(--efisio-navy)]">
        Não conseguimos carregar este artigo
      </h1>
      <p className="mt-3 text-[var(--efisio-muted)]">Tente novamente em alguns instantes.</p>
    </main>
  );
}

function ArtigoNaoEncontrado() {
  return (
    <main className="container mx-auto px-4 py-24 text-center">
      <h1 className="text-2xl font-extrabold text-[var(--efisio-navy)]">Artigo não encontrado</h1>
      <p className="mt-3 text-[var(--efisio-muted)]">
        O endereço que você acessou não corresponde a nenhum artigo publicado no E-FISIO.
      </p>
      <Link
        to="/e-fisio"
        className="mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-[var(--efisio-blue)]"
      >
        <ArrowLeft className="h-4 w-4" /> Ver todos os artigos
      </Link>
    </main>
  );
}

function ArtigoPage() {
  const { slug } = Route.useParams();
  const { data } = useSuspenseQuery(blogPostQuery(slug));
  const [nivel, setNivel] = useState(ESCALA_PADRAO);
  useEffect(() => {
    const s = Number(localStorage.getItem("efisio-texto"));
    if (Number.isInteger(s) && s >= 0 && s < ESCALAS.length) setNivel(s);
  }, []);
  const mudarNivel = (n: number) => {
    setNivel(n);
    localStorage.setItem("efisio-texto", String(n));
  };
  const post = data.post;
  if (!post) return <ArtigoNaoEncontrado />;

  const cat = data.categories.find((c) => c.slug === post.categoria_slugs?.[0]);
  const autor = [post.autor, post.autor_cidade].filter(Boolean).join(", ");

  const md = post.conteudo_md ?? "";
  const { minutos } = tempoLeitura(md);
  
  const { intro } = separarIntroducao(md, post.subtitulo);
  const introTexto = intro
    ? intro.replace(/\[([^\]]*)\]\([^)]*\)/g, "$1").replace(/\*\*|__|(^|\s)[_*](\S)/g, (m, a, b) => (b ? a + b : "")).trim()
    : null;
  const corpo = separarIntroducao(md, post.subtitulo).corpo;
  const [parte1, parte2] = dividirNoMeio(corpo);
  const ancoras = new Set<string>();

  return (
    <main>
      <ReadingProgress />
      <BackToTop />
      <TamanhoTexto nivel={nivel} onChange={mudarNivel} />

      <BlogBanner />

      <div style={{ zoom: ESCALAS[nivel] }}>
      <header className="container mx-auto px-4 pt-10 sm:pt-14">
        <div className="mx-auto grid w-full max-w-[52rem] items-center gap-6 sm:grid-cols-[1fr_220px] sm:gap-8">
          <div>
            <nav aria-label="Trilha de navegação" className="mb-4">
              <ol className="flex flex-wrap items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-[var(--efisio-muted)]">
                <li>
                  <Link to="/e-fisio" className="text-[var(--efisio-blue)] hover:underline">
                    E-FISIO
                  </Link>
                </li>
                {cat && (
                  <>
                    <li aria-hidden="true">/</li>
                    <li>
                      <Link
                        to="/e-fisio/categoria/$slug"
                        params={{ slug: cat.slug }}
                        className="text-[var(--efisio-blue)] hover:underline"
                      >
                        {cat.nome}
                      </Link>
                    </li>
                  </>
                )}
                <li aria-hidden="true">·</li>
                <li>{minutos} min de leitura</li>
              </ol>
            </nav>
            <h1 className="text-3xl font-extrabold leading-tight tracking-tight text-[var(--efisio-navy)] sm:text-[2.2rem]">
              {post.titulo}
            </h1>
            {introTexto && (
              <p className="mt-4 text-base leading-relaxed text-[var(--efisio-muted)] sm:text-lg">{introTexto}</p>
            )}
            {autor && <p className="mt-4 text-sm font-semibold text-[var(--efisio-ink)]">Por: {autor}</p>}
          </div>
          {post.capa_url && (
            <img
              src={post.capa_url}
              alt={post.titulo}
              width={1600}
              height={900}
              decoding="async"
              className="aspect-square w-full rounded-2xl object-cover shadow-md sm:aspect-[4/5]"
            />
          )}
        </div>
      </header>

      <div className="container mx-auto px-4 py-10 sm:py-12">
        <div className="lg:mx-auto lg:grid lg:max-w-[72rem] lg:grid-cols-[15rem_minmax(0,52rem)] lg:gap-10">
          <aside className="hidden lg:block">
            <Toc itens={extrairToc(md)} variante="lateral" />
          </aside>
          <article className="mx-auto w-full max-w-[52rem]">
            <div className="mb-6">
              <EmResumo
                topicos={extrairResumo(md)}
                categorias={post.categoria_slugs}
              />
            </div>
            <div className="mb-10 lg:hidden">
              <IndiceArtigo itens={extrairToc(md)} />
            </div>


            <Lightbox />
            <Markdown usados={ancoras}>{parte1}</Markdown>

            {parte2 && (
              <>
                <div className="my-12">
                  <PosCta categorias={post.categoria_slugs} posicao="meio" />
                </div>
                <Markdown usados={ancoras}>{parte2}</Markdown>
              </>
            )}

            <div className="mt-12 border-t border-[var(--efisio-border)] pt-6">
              <ShareButtons url={`${BASE}/e-fisio/${post.slug}`} titulo={post.titulo} />
            </div>

            <AuthorBox autor={post.autor} cidade={post.autor_cidade} />

            <div className="mt-12">
              <PosCta categorias={post.categoria_slugs} />
            </div>

          </article>
        </div>
      </div>
      </div>

      {data.related.length > 0 && (
        <section className="border-t border-[var(--efisio-border)] bg-[var(--efisio-surface)]">
          <div className="container mx-auto px-4 py-12 sm:py-16">
            <h2 className="text-xl font-extrabold uppercase tracking-wide text-[var(--efisio-navy)]">
              Leia também
            </h2>
            <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {data.related.map((p) => (
                <PostCard key={p.slug} post={p} categories={data.categories} />
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
